import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
    Users, Building2, Search, MoreVertical, 
    Key, LogIn, Loader2, Shield, ArrowLeft
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function UserManagement() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    
    // Reset password modal
    const [resetUser, setResetUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [resetting, setResetting] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await api.get('/super-admin/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        
        setResetting(true);
        try {
            await api.post(`/super-admin/users/${resetUser.id}/reset-password`, {
                new_password: newPassword
            });
            toast.success('Password reset successfully');
            setResetUser(null);
            setNewPassword('');
        } catch (error) {
            console.error('Failed to reset password:', error);
            toast.error('Failed to reset password');
        } finally {
            setResetting(false);
        }
    };

    const handleImpersonate = async (user) => {
        try {
            const response = await api.post(`/super-admin/impersonate/${user.id}`);
            localStorage.setItem('fleet_token', response.data.access_token);
            localStorage.setItem('fleet_user', JSON.stringify(response.data.user));
            localStorage.setItem('impersonating', 'true');
            toast.success(`Now viewing as ${response.data.user.name}`);
            window.location.href = '/app/dashboard';
        } catch (error) {
            console.error('Failed to impersonate:', error);
            toast.error('Failed to impersonate user');
        }
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = 
            user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = filterRole === 'all' || user.role === filterRole;
        return matchesSearch && matchesRole;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="user-management">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => navigate('/app/dashboard')}
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Shield className="w-5 h-5 text-purple-600" />
                            <span className="text-sm text-purple-600 font-medium">Super Admin</span>
                        </div>
                        <h1 className="text-2xl font-bold text-slate-800">All Users</h1>
                        <p className="text-slate-500">{users.length} total users across all companies</p>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                        placeholder="Search by name, email, or company..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-white border-slate-300"
                    />
                </div>
                <Select value={filterRole} onValueChange={setFilterRole}>
                    <SelectTrigger className="w-48 bg-white border-slate-300">
                        <SelectValue placeholder="All Roles" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="fleet_manager">Fleet Manager</SelectItem>
                        <SelectItem value="driver">Driver</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Users Table */}
            <div className="fleet-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Company</th>
                                <th>Role</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((user) => (
                                <tr key={user.id} className="table-row-hover">
                                    <td>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-[#1B7EB5]/10 rounded-full flex items-center justify-center text-[#1B7EB5] font-medium">
                                                {user.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <p className="font-medium text-slate-800">{user.name}</p>
                                                <p className="text-xs text-slate-500">{user.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-2">
                                            <Building2 className="w-4 h-4 text-slate-400" />
                                            <span className="text-slate-600">{user.company_name || 'No Company'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                                            user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                                            user.role === 'fleet_manager' ? 'bg-blue-100 text-blue-700' :
                                            'bg-slate-100 text-slate-700'
                                        }`}>
                                            {user.role.replace('_', ' ')}
                                        </span>
                                    </td>
                                    <td className="text-slate-500">
                                        {new Date(user.created_at).toLocaleDateString()}
                                    </td>
                                    <td>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                                    <MoreVertical className="w-4 h-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="bg-white border-slate-200">
                                                <DropdownMenuItem onClick={() => handleImpersonate(user)}>
                                                    <LogIn className="w-4 h-4 mr-2" />
                                                    Login As User
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => setResetUser(user)}>
                                                    <Key className="w-4 h-4 mr-2" />
                                                    Reset Password
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Reset Password Modal */}
            <Dialog open={!!resetUser} onOpenChange={() => {setResetUser(null); setNewPassword('');}}>
                <DialogContent className="bg-white border-slate-200 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <Key className="w-5 h-5 text-[#1B7EB5]" />
                            Reset Password
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div className="bg-slate-50 rounded-lg p-3">
                            <p className="text-sm text-slate-600">Resetting password for:</p>
                            <p className="font-medium text-slate-800">{resetUser?.name}</p>
                            <p className="text-xs text-slate-500">{resetUser?.email}</p>
                        </div>
                        <div>
                            <label className="text-sm text-slate-600">New Password</label>
                            <Input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password (min 6 characters)"
                                className="bg-white border-slate-300"
                            />
                        </div>
                        <div className="flex gap-3 pt-2">
                            <Button variant="outline" onClick={() => {setResetUser(null); setNewPassword('');}} className="flex-1">
                                Cancel
                            </Button>
                            <Button onClick={handleResetPassword} disabled={resetting} className="flex-1 bg-[#1B7EB5] text-white">
                                {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
